import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';

// Define the structure for error objects
export interface ErrorDetail {
  id: string;
  message: string;
  component?: string;
  stack?: string;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

// Define the context state structure
interface ErrorContextState {
  errors: Record<string, ErrorDetail>;
  addError: (error: Omit<ErrorDetail, 'id' | 'timestamp'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
  hasErrors: boolean;
  getErrorsForComponent: (componentName: string) => ErrorDetail[];
}

// Create the context with a default value
const ErrorContext = createContext<ErrorContextState | undefined>(undefined);

// Provider props interface
interface ErrorProviderProps {
  children: ReactNode;
}

/**
 * ErrorProvider component that manages the error state
 * @param {ReactNode} children - Child components
 */
export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  // State to store all errors
  const [errors, setErrors] = useState<Record<string, ErrorDetail>>({});

  // Compute if there are any errors
  const hasErrors = Object.keys(errors).length > 0;

  /**
   * Add a new error to the context
   * @param {Omit<ErrorDetail, 'id' | 'timestamp'>} error - Error details
   * @returns {string} The ID of the newly added error
   */
  const addError = useCallback(
    (error: Omit<ErrorDetail, 'id' | 'timestamp'>): string => {
      const id = `error-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const timestamp = Date.now();

      setErrors((prev) => ({
        ...prev,
        [id]: { ...error, id, timestamp },
      }));

      console.error('Error tracked:', { ...error, id, timestamp });
      return id;
    },
    []
  );

  /**
   * Remove a specific error by ID
   * @param {string} id - Error ID to remove
   */
  const removeError = useCallback((id: string): void => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  }, []);

  /**
   * Clear all errors from the context
   */
  const clearErrors = useCallback((): void => {
    setErrors({});
  }, []);

  /**
   * Get all errors for a specific component
   * @param {string} componentName - Name of the component
   * @returns {ErrorDetail[]} Array of errors for the component
   */
  const getErrorsForComponent = useCallback(
    (componentName: string): ErrorDetail[] => {
      return Object.values(errors).filter(
        (error) => error.component === componentName
      );
    },
    [errors]
  );

  // Create the context value object
  const contextValue: ErrorContextState = {
    errors,
    addError,
    removeError,
    clearErrors,
    hasErrors,
    getErrorsForComponent,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

/**
 * Hook to use the error context
 * @returns {ErrorContextState} Error context state and functions
 * @throws {Error} If used outside of ErrorProvider
 */
export const useErrorContext = (): ErrorContextState => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
};
